import LocationSelect from '../../../reusable/LocationSelect';
import FormField from './FormField';

const ProfileSection = ({
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    setFieldValue,
    userInfo,
    handleRoleChange,
    dateThreshold,
    DropdownWithCheckBox,
    PreferredFoot,
    PlayerPosition,
    cleanDate
}) => {
    return (
        <section className='profile-section'>
            {userInfo.userType !== 'NON_PLAYER' && (
                <h3 className='text-[14px] font-semibold my-5'>Profile</h3>
            )}

            <div className='grid grid-cols-2 gap-6'>
                <FormField
                    name='firstName'
                    label='First Name'
                    value={values?.firstName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={errors.firstName}
                    touched={touched.firstName}
                    required
                    helpText='2-50 characters'
                />

                <FormField
                    name='lastName'
                    label='Last Name'
                    value={values?.lastName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={errors.lastName}
                    touched={touched.lastName}
                    required
                    helpText='2-50 characters'
                />
            </div>

            {userInfo.userType !== 'NON_PLAYER' && (
                <div className='grid grid-cols-2 gap-6 items-end'>
                    <FormField
                        name='preferredFoot'
                        label='Preferred Foot'
                        error={errors.preferredFoot}
                        touched={touched.preferredFoot}
                        required
                    >
                        <DropdownWithCheckBox
                            label='Select Foot'
                            allowMultiple={false}
                            allowSearch={false}
                            defaultOptions={
                                userInfo.preferredFoot
                                    ? [userInfo.preferredFoot]
                                    : []
                            }
                            listOfOptions={
                                Object.entries(PreferredFoot).map(
                                    ([value, label]) => ({
                                        value,
                                        label
                                    })
                                ) || []
                            }
                            valueKey='value'
                            labelKey='label'
                            onChange={options => {
                                // Handle when options is empty (user deselected) or has values
                                const selectedValue =
                                    options && options.length > 0
                                        ? options[0]
                                        : '';
                                setFieldValue('preferredFoot', selectedValue);
                            }}
                        />
                    </FormField>

                    <FormField
                        name='height'
                        label='Height (m)'
                        inputMode='decimal'
                        required
                        value={values?.height}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder='e.g. 1.83'
                        error={errors.height}
                        touched={touched.height}
                        type='text'
                        helpText='Enter height in meters (e.g. 1.83)'
                    />
                </div>
            )}

            <div
                className={`grid ${
                    userInfo.userType !== 'NON_PLAYER'
                        ? 'grid-cols-2'
                        : 'grid-cols-1'
                } gap-6 items-end`}
            >
                {/* DATE OF BIRTH & LOCATION */}
                {userInfo.userType !== 'NON_PLAYER' && (
                    <FormField
                        name='birthday'
                        required
                        label='Date of Birth'
                        error={errors.birthday}
                        touched={touched.birthday}
                        placeholder='e.g., 2000-01-01'
                    >
                        <input
                            type='date'
                            onChange={e => {
                                setFieldValue(
                                    'birthday',
                                    cleanDate(e.target.value)
                                );
                            }}
                            value={cleanDate(values.birthday)}
                            max={dateThreshold}
                        />
                    </FormField>
                )}

                <FormField
                    name='locationId'
                    label='Location'
                    addBottomBorder={false}
                    error={errors.locationId}
                    touched={touched.locationId}
                    required
                >
                    <LocationSelect
                        selectedLocation={{
                            id: values.locationId,
                            name: values.location
                        }}
                        setSelectedLocation={selectedLocation => {
                            setFieldValue('location', selectedLocation.name);
                            setFieldValue('locationId', selectedLocation.id);
                            setFieldValue(
                                'district',
                                selectedLocation.district
                            );
                            setFieldValue(
                                'locality',
                                selectedLocation.locality
                            );
                            setFieldValue('region', selectedLocation.region);
                            setFieldValue('country', selectedLocation.country);
                            setFieldValue(
                                'countryCode',
                                selectedLocation.countryCode
                            );
                        }}
                        borderColor={
                            errors.locationId && touched.locationId
                                ? 'border-red-600'
                                : ''
                        }
                        className='absolute z-10'
                        onBlur={handleBlur}
                    />
                </FormField>
            </div>

            {/* POSITIONS */}
            {userInfo.userType !== 'NON_PLAYER' && (
                <div className='grid grid-cols-2 gap-6'>
                    <FormField
                        name='position'
                        label='Primary Position'
                        error={errors.position}
                        required
                        touched={touched.position}
                    >
                        <DropdownWithCheckBox
                            label='Select position'
                            allowMultiple={false}
                            allowSearch={false}
                            defaultOptions={
                                userInfo.position ? [userInfo.position] : []
                            }
                            listOfOptions={
                                Object.entries(PlayerPosition).map(
                                    ([value, label]) => ({
                                        value,
                                        label
                                    })
                                ) || []
                            }
                            valueKey='value'
                            labelKey='label'
                            onChange={options => {
                                // Handle when options is empty (user deselected) or has values
                                const selectedValue =
                                    options && options.length > 0
                                        ? options[0]
                                        : '';
                                setFieldValue('position', selectedValue);

                                // Remove the selected primary position from secondary positions if it exists
                                if (
                                    selectedValue &&
                                    values.secondaryPositions
                                ) {
                                    const updatedSecondary =
                                        values.secondaryPositions.filter(
                                            pos => pos !== selectedValue
                                        );
                                    if (
                                        updatedSecondary.length !==
                                        values.secondaryPositions.length
                                    ) {
                                        setFieldValue(
                                            'secondaryPositions',
                                            updatedSecondary
                                        );
                                    }
                                }
                            }}
                        />
                    </FormField>

                    <FormField
                        name='secondaryPositions'
                        label='Secondary Position'
                    >
                        <DropdownWithCheckBox
                            id='secondaryPositions'
                            label='Select position(s)'
                            allowMultiple={true}
                            maxSelection={3}
                            allowSearch={false}
                            defaultOptions={userInfo.secondaryPositions || []}
                            listOfOptions={
                                Object.entries(PlayerPosition).map(
                                    ([value, label]) => ({
                                        value,
                                        label,
                                        disabled: value === values.position
                                    })
                                ) || []
                            }
                            valueKey='value'
                            labelKey='label'
                            onChange={options =>
                                handleRoleChange(options, 'secondaryPositions')
                            }
                        />
                    </FormField>
                </div>
            )}

            {/* BIO */}
            {userInfo.userType !== 'NON_PLAYER' && (
                <FormField
                    type='textarea'
                    name='bio'
                    label='Bio'
                    value={values?.bio}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder='Write about yourself (10-500 characters)'
                    error={errors.bio}
                    touched={touched.bio}
                    helpText='Tell us about yourself (10-500 characters)'
                />
            )}
        </section>
    );
};

export default ProfileSection;
